import { useRef, useEffect, useCallback } from "react";
import { usePaneStore, useVerificationStoreApi } from "../state/store-context";
import { renderFrame } from "../canvas/renderer";
import {
  zoomAtPoint,
  fitToWidth,
  fitToPage,
  centerOnZone,
  isZoneVisible,
} from "../canvas/viewport";
import { useViewerZoomPersist } from "../hooks/useViewerZoomPersist";
import type { BoundingBox, FieldState, FitMode, Viewport } from "../state/types";

// =============================================================================
// R-163 — filter helpers hoisted for unit-testing the null/undefined guard.
//
// Earlier code wrote `f.box !== null && f.box.pageNumber === ...` which is a
// trap: when `f.box` is `undefined` (not `null`), the strict !== check passes
// and `f.box.pageNumber` throws TypeError on "Get next document." Using
// `!= null` catches both. The exported helpers below keep the predicate honest
// and let us assert the behavior without instantiating the canvas component.
// =============================================================================

export interface FieldBoxEntry {
  box: BoundingBox;
  isActive: boolean;
  confidence: number | null;
}

export interface ZoneBoxEntry {
  zone: BoundingBox;
  isActive: boolean;
}

export function filterFieldBoxesForPage(
  fields: ReadonlyArray<Pick<FieldState, "boundingBox" | "ocrConfidence">>,
  activeFieldIndex: number,
  pageNumber: number,
): FieldBoxEntry[] {
  return fields
    .map((f, i) => ({
      box: f.boundingBox,
      isActive: i === activeFieldIndex,
      confidence: f.ocrConfidence,
    }))
    .filter(
      (f): f is FieldBoxEntry =>
        f.box != null && f.box.pageNumber === pageNumber,
    );
}

export function filterZoneBoxesForPage(
  fields: ReadonlyArray<Pick<FieldState, "zoneDefinition">>,
  activeFieldIndex: number,
  pageNumber: number,
): ZoneBoxEntry[] {
  return fields
    .map((f, i) => ({
      zone: f.zoneDefinition,
      isActive: i === activeFieldIndex,
    }))
    .filter(
      (f): f is ZoneBoxEntry =>
        f.zone != null && f.zone.pageNumber === pageNumber,
    );
}

// =============================================================================
// F4 (audit 2026-05-12) — DocumentCanvas HARD-REQUIRES Page rows from
// image-process. The canvas reads `page.imageBitmap` for bbox math (see
// `page.imageBitmap.width` references below) and the parent VerificationStation
// passes page.imagePath → ImageBitmap-loading code. If image-process has
// never run for a document (no Page rows, no imagePath), the verifier shows
// nothing and operators cannot key.
//
// This is a documented dependency, not a defect — F1's poppler fix makes
// image-process reliable. If a future refactor proposes "skip image-process
// for OCR-only batches" or "render PDFs client-side without server-side
// rasterization," it MUST first reckon with this contract.
// =============================================================================

/**
 * The fit-precedence rule, in ONE place. Three call sites need it — the
 * bitmap-load effect, the ResizeObserver re-fit, and the explicit Fit signal —
 * and before this they each carried their own copy, which is how R-197 (fitMode
 * ignored on bitmap load) and R-165 (savedViewerZoom ignored on resize) each
 * got fixed in one copy and not the others.
 *
 * Precedence, highest first:
 *   1. R-149/R-165 — a saved zoom preference wins outright (pass `null` at
 *      sites where the fit is the operator's explicit choice; toggleFitMode
 *      clears the saved zoom for exactly that reason).
 *   2. R-197 — otherwise the operator's fitMode: "page" fits both axes (so a
 *      short viewport actually shrinks the page), "width" fits the width.
 *
 * `pane` is the VIEWER PANE's box (F-060) — never the canvas's.
 */
export function resolveFitViewport(
  viewport: Viewport,
  fitMode: FitMode,
  savedViewerZoom: number | null,
  bitmap: { width: number; height: number },
  pane: { width: number; height: number },
): Viewport {
  if (savedViewerZoom !== null) {
    return { ...viewport, scale: savedViewerZoom, x: 0, y: 0 };
  }
  return fitMode === "page"
    ? fitToPage(viewport, bitmap.width, bitmap.height, pane.width, pane.height)
    : fitToWidth(viewport, bitmap.width, pane.width);
}

export function DocumentCanvas() {
  // SP3 §4 — bind imperative getState()/setViewport reads to THIS pane's store
  // instance, not the module default. Standalone /verify (no provider) falls
  // back to the default instance, so behavior there is unchanged.
  const storeApi = useVerificationStoreApi();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const animRef = useRef<number>(0);
  const isDragging = useRef(false);
  const lastMouse = useRef({ x: 0, y: 0 });
  // R-188 — skip the first effect run (initial mount) so auto-center never
  // blows away the operator's chosen starting position; only pan on subsequent
  // focus changes.
  const hasAutoPannedRef = useRef(false);

  const pages = usePaneStore((s) => s.pages);
  const currentPageIndex = usePaneStore((s) => s.currentPageIndex);
  const viewport = usePaneStore((s) => s.viewport);
  const setViewport = usePaneStore((s) => s.setViewport);
  const fields = usePaneStore((s) => s.fields);
  const activeFieldIndex = usePaneStore((s) => s.activeFieldIndex);
  const setCurrentPage = usePaneStore((s) => s.setCurrentPage);
  // R-199 — flip the manual-zoom override on explicit zoom actions so the
  // ResizeObserver re-fit defers to the operator's chosen zoom.
  const setManualZoomActive = usePaneStore((s) => s.setManualZoomActive);
  const showBboxOverlays = usePaneStore((s) => s.showBboxOverlays);
  // R-149 — operator's persisted preferred zoom (viewport.scale).
  const savedViewerZoom = usePaneStore((s) => s.savedViewerZoom);
  // R-148 — signal from the VS toolbar's "Fit Width" button.
  const fitToWidthRequest = usePaneStore((s) => s.fitToWidthRequest);

  // R-149 — debounced persist callback wired into user-initiated zoom actions.
  const persistZoom = useViewerZoomPersist();

  const currentPage = pages[currentPageIndex];

  // ===========================================================================
  // F-060 — measure the VIEWER PANE, never the canvas.
  //
  // Driver: "the VS still opens documents too wide. it is almost as if the
  // width of the image is calculated prior to removing the width of the keying
  // panel." That is very nearly the literal mechanism. Every fit site used to
  // call `canvas.getBoundingClientRect()`, but the canvas is a REPLACED element
  // whose intrinsic width is its `width` attribute — which this component sets
  // to `paneWidth * devicePixelRatio` a few lines below. With `.vs-document`
  // on the default `min-width: auto`, that intrinsic width became the pane's
  // content-based minimum, so the pane could not give the keying panel its
  // 340px and the canvas box stayed at (or above) the pre-panel width. Fitting
  // to that box fits to a width the operator does not have.
  //
  // The outer container is a plain <div> — it has no intrinsic width, so its
  // box is the true post-layout pane. Measuring it is correct even after the
  // CSS fixes remove the inflation, and it stays correct through panel resize,
  // collapse, detach, and window resize because the ResizeObserver below feeds
  // the same measurement back in.
  // ===========================================================================
  const measurePane = useCallback((): { width: number; height: number } => {
    const el = containerRef.current;
    if (!el) return { width: 0, height: 0 };
    const r = el.getBoundingClientRect();
    return { width: r.width, height: r.height };
  }, []);

  // R-188 — auto-center the page image on the focused field's zone as the
  // operator tabs between fields. Reads live store state so the pan uses the
  // operator's current zoom, honors the `vsAutoCenterOnFocus` opt-out, and
  // skips the initial mount (hasAutoPannedRef). The shared `isZoneVisible` /
  // `centerOnZone` helpers own the geometry.
  useEffect(() => {
    if (!hasAutoPannedRef.current) {
      hasAutoPannedRef.current = true;
      return;
    }
    const state = storeApi.getState();
    if (!state.vsAutoCenterOnFocus) return;

    const bbox = state.fields[activeFieldIndex]?.boundingBox;
    if (!bbox) return;

    if (bbox.pageNumber !== state.currentPageIndex + 1) {
      setCurrentPage(bbox.pageNumber - 1);
    }

    // F-060 — the pane is the box the operator can actually see, so it is the
    // box "is this zone visible / where do I centre it" must reason about.
    const rect = measurePane();
    if (rect.width <= 0) return;

    const page = state.pages[bbox.pageNumber - 1];
    if (!page?.imageBitmap) return;

    const pageDims = { width: page.imageBitmap.width, height: page.imageBitmap.height };
    const canvasDims = { width: rect.width, height: rect.height };
    if (isZoneVisible(state.viewport, bbox, pageDims, canvasDims)) return;
    setViewport(centerOnZone(state.viewport, bbox, pageDims, canvasDims));
  }, [activeFieldIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Build bounding box list for current page.
  // R-163 — use exported helpers with `!= null` so an undefined boundingBox
  // (which previously slipped past `!== null`) no longer crashes the canvas
  // on "Get next document."
  const fieldBoxes = filterFieldBoxesForPage(
    fields,
    activeFieldIndex,
    currentPageIndex + 1,
  );

  // R-177 — admin-drawn zones for the current page. Always-on layer (no
  // visibility toggle) so operators can see configured extraction regions
  // alongside whatever OCR returned. Mirrors the Zones tab's copper/slate
  // convention via the renderer.
  const zoneBoxes = filterZoneBoxesForPage(
    fields,
    activeFieldIndex,
    currentPageIndex + 1,
  );

  // Render loop
  const draw = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    renderFrame({
      ctx,
      viewport,
      pageBitmap: currentPage?.imageBitmap ?? null,
      pageWidth: currentPage?.width ?? 0,
      pageHeight: currentPage?.height ?? 0,
      canvasWidth: canvas.width,
      canvasHeight: canvas.height,
      fieldBoxes,
      zoneBoxes,
      showBboxOverlays,
      // F-110 — read live rather than captured: dragging a window between a
      // Retina and an external display changes it mid-session.
      devicePixelRatio: window.devicePixelRatio || 1,
    });
  }, [viewport, currentPage, fieldBoxes, zoneBoxes, showBboxOverlays]);

  // Keep a ref to the latest `draw` so long-lived callbacks (e.g. the
  // ResizeObserver) can call it without participating in the effect's dep
  // graph (and thus without tearing down/recreating the observer on every
  // viewport/field change).
  const drawRef = useRef(draw);
  useEffect(() => {
    drawRef.current = draw;
  }, [draw]);

  useEffect(() => {
    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [draw]);

  // R-148 — Resize observer wires both the OUTER container and the canvas.
  // Observing the container catches layout shifts from the field-panel drawer
  // expanding/collapsing and from Detach Panel popping the side panel out,
  // which leave the canvas itself with the same CSS size but a different
  // pixel size on the next frame. Observing both means we never miss a
  // size change regardless of which way the browser reports it.
  //
  // We use `useVerificationStore.getState()` inside the observer callback
  // to read live `viewport` / `pages` / `currentPageIndex` instead of
  // closure-captured values — the observer outlives the effect closure
  // and stale captures were a source of "image vanishes on toggle".
  useEffect(() => {
    const canvas = canvasRef.current;
    const container = containerRef.current;
    if (!canvas) return;

    const handleResize = () => {
      // F-060 — the pane, not the canvas (the canvas box is what we're about
      // to resize, so reading it here would be reading our own output).
      const rect = measurePane();
      if (rect.width <= 0 || rect.height <= 0) return;

      // Assigning canvas.width/height RESETS the backing store even when the
      // value is unchanged (allocate + clear + full redraw), and the observer
      // fires on layout shifts that don't move the pane at all. Only pay for it
      // when the size actually changed.
      const bufferWidth = rect.width * devicePixelRatio;
      const bufferHeight = rect.height * devicePixelRatio;
      if (canvas.width !== bufferWidth || canvas.height !== bufferHeight) {
        canvas.width = bufferWidth;
        canvas.height = bufferHeight;
        // F-110 — no ctx.scale(dpr) here. Assigning width/height resets the
        // context anyway, and `renderFrame` re-bases the transform to dpr on
        // every frame (applyTransform), so a scale applied here was discarded
        // one frame later — it only ever read as if the ratio were handled.
      }

      // Re-fit using LIVE store state (avoids stale-closure clipping).
      const state = storeApi.getState();
      const page = state.pages[state.currentPageIndex];
      if (!page?.imageBitmap) {
        // No bitmap yet — schedule a redraw anyway so we don't leave a blank
        // resized buffer behind.
        animRef.current = requestAnimationFrame(() => drawRef.current());
        return;
      }

      // R-199 — the operator explicitly zoomed via the controls. A manual
      // zoom is an override that wins over the auto-fit until they pick a Fit
      // mode again (toggleFitMode clears the flag) or load a new doc. Bail
      // BEFORE recomputing so a drawer/detach toggle can't snap their zoom
      // away. This covers the window where savedViewerZoom hasn't been
      // persisted yet (its 500ms debounce is still in flight).
      if (state.manualZoomActive) {
        animRef.current = requestAnimationFrame(() => drawRef.current());
        return;
      }

      // Drawer/detach/window resize re-fits to the NEW pane size, respecting
      // the operator's saved zoom and chosen fit mode (R-165/R-197).
      state.setViewport(
        resolveFitViewport(
          state.viewport,
          state.fitMode,
          state.savedViewerZoom,
          page.imageBitmap,
          rect,
        ),
      );

      // Force a redraw on the new buffer in case viewport didn't change.
      animRef.current = requestAnimationFrame(() => drawRef.current());
    };

    const observer = new ResizeObserver(handleResize);
    // R-148 — observe the OUTER container: it is what the field-panel drawer /
    // Detach Panel toggles actually resize.
    //
    // F-060 — and observe ONLY it. The canvas is now `position: absolute;
    // inset: 0` on this container, so it carries no independent size signal —
    // but `handleResize` WRITES canvas.width/height, so observing the canvas
    // meant the handler re-triggered itself, running twice (two buffer
    // reallocations, two redraws) on every panel drag.
    if (container) observer.observe(container);
    return () => observer.disconnect();
  }, []);

  // Auto-fit when the image first loads. R-199 — an active manual-zoom
  // override is left untouched (a detach/reattach remounts the canvas
  // mid-session; don't snap the operator's zoom away); everything else is
  // `resolveFitViewport`'s precedence rule.
  useEffect(() => {
    if (!currentPage?.imageBitmap) return;
    // F-060 — pane, not canvas. If the pane still measures 0 (bitmap arrived
    // before layout settled) we skip; the ResizeObserver fires the moment it
    // has a real width and performs the fit then.
    const rect = measurePane();
    if (rect.width <= 0) return;

    const state = storeApi.getState();
    if (state.manualZoomActive) return;

    setViewport(
      resolveFitViewport(
        viewport,
        state.fitMode,
        savedViewerZoom !== null && savedViewerZoom > 0 ? savedViewerZoom : null,
        currentPage.imageBitmap,
        rect,
      ),
    );
  }, [currentPage?.imageBitmap]); // eslint-disable-line react-hooks/exhaustive-deps

  // R-148 / R-164 — toolbar "Fit Width / Fit Page" button increments
  // fitToWidthRequest. Fires a re-fit using current container dimensions,
  // dispatching on `fitMode`. We intentionally do NOT call persistZoom here:
  // the fit is a chosen behavior (not a manual zoom), so the operator's saved
  // zoom should be cleared (toggleFitMode handles that) rather than being
  // re-pinned to the fit scale. Manual wheel/double-click will still pin
  // their preferred zoom afterwards.
  useEffect(() => {
    if (fitToWidthRequest === 0) return; // skip initial mount
    if (!currentPage?.imageBitmap) return;
    const rect = measurePane(); // F-060 — pane, not canvas
    if (rect.width <= 0) return;

    // `null` for the saved zoom: this fit IS the operator's explicit choice,
    // so it must not be short-circuited by an older saved zoom (toggleFitMode
    // clears it for the same reason).
    setViewport(
      resolveFitViewport(
        viewport,
        storeApi.getState().fitMode,
        null,
        currentPage.imageBitmap,
        rect,
      ),
    );
  }, [fitToWidthRequest]); // eslint-disable-line react-hooks/exhaustive-deps

  // Mouse wheel zoom
  const handleWheel = useCallback(
    (e: React.WheelEvent) => {
      e.preventDefault();
      const canvas = canvasRef.current;
      if (!canvas) return;
      const rect = canvas.getBoundingClientRect();
      const canvasX = e.clientX - rect.left;
      const canvasY = e.clientY - rect.top;
      const newViewport = zoomAtPoint(viewport, canvasX, canvasY, e.deltaY);
      setViewport(newViewport);
      // R-199 — explicit manual zoom: protect it from the drawer-resize re-fit.
      setManualZoomActive(true);
      // R-149 — persist the new zoom level for cross-document carry-over.
      persistZoom(newViewport.scale);
    },
    [viewport, setViewport, setManualZoomActive, persistZoom],
  );

  // Pan: mouse down/move/up
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      isDragging.current = true;
      lastMouse.current = { x: e.clientX, y: e.clientY };
    },
    [],
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging.current) return;
      const dx = e.clientX - lastMouse.current.x;
      const dy = e.clientY - lastMouse.current.y;
      lastMouse.current = { x: e.clientX, y: e.clientY };
      setViewport({ x: viewport.x + dx, y: viewport.y + dy });
    },
    [viewport, setViewport],
  );

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
  }, []);

  // Double-click fit to width
  const handleDoubleClick = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas || !currentPage) return;
    const rect = measurePane(); // F-060 — pane, not canvas
    const newViewport = fitToWidth(viewport, currentPage.width, rect.width);
    setViewport(newViewport);
    // R-199 — double-click IS an explicit fit-to-width, so clear the manual
    // override: subsequent drawer/detach resizes should re-fit, not freeze.
    setManualZoomActive(false);
    // R-149 — persist the fit-to-width zoom so it carries to the next doc.
    persistZoom(newViewport.scale);
  }, [viewport, currentPage, setViewport, setManualZoomActive, persistZoom, measurePane]);

  return (
    <div ref={containerRef} className="document-canvas-container">
      <canvas
        ref={canvasRef}
        className="document-canvas"
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onDoubleClick={handleDoubleClick}
      />
      {pages.length > 1 && (
        <div className="page-nav">
          {pages.map((p, i) => (
            <button
              key={p.id}
              className={`page-nav-btn ${i === currentPageIndex ? "active" : ""}`}
              onClick={() => setCurrentPage(i)}
            >
              {p.pageNumber}
            </button>
          ))}
        </div>
      )}

      <style>{`
        .document-canvas-container {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          background: var(--color-bg-primary);
        }
        /* F-060 — absolutely positioned ON PURPOSE: it takes the canvas out of
           flow so its backing-store width can never size the viewer pane. See
           the measurePane() comment above for the full mechanism. The
           container is position: relative, so inset: 0 means "fill the pane"
           and is the sole sizing statement (no width/height needed). */
        .document-canvas {
          position: absolute;
          inset: 0;
          cursor: grab;
          display: block;
        }
        .document-canvas:active { cursor: grabbing; }
        .page-nav {
          position: absolute;
          bottom: var(--space-3);
          left: 50%;
          transform: translateX(-50%);
          display: flex;
          gap: var(--space-1);
          background: var(--color-bg-elevated);
          border: 1px solid var(--color-border);
          border-radius: var(--radius-sm);
          padding: var(--space-1);
        }
        .page-nav-btn {
          width: 28px;
          height: 24px;
          font-size: 0.75rem;
          border: none;
          background: transparent;
          color: var(--color-text-secondary);
          border-radius: var(--radius-sm);
          cursor: pointer;
        }
        .page-nav-btn.active {
          background: var(--color-primary);
          color: white;
        }
      `}</style>
    </div>
  );
}
